(() => {
  /* TSR Sparkle-Plus — ADDITIVE enhancement layer (2026-07-04).
     1) Overlay grain/glint canvas on every .tsr-salt-host — base engine untouched.
        Also seasons the masthead hero PRIMARY button with a whisper of drift,
        and applies the SECONDARY button's frosted-glass background inline
        (the section stylesheet wins every cascade war — inline-important is
        the only clean override; hover handled here too).
     2) Footer campfire scene: extra stars + twinkles + glimmer; hero star
        breathes, and a SHOOTING STAR streaks the sky after ~5s of being
        watched (replaced the in-place bright burst — Koby 2026-07-05).
        seatSmoke(): wisps re-seated 5 units lower so smoke emerges from the
        fire, not above it (Koby 2026-07-06; first pass used 12 — too deep,
        strong wisp bottoms draped over the LOG FACES because the logs paint
        before ALL smoke — 5 lands the bottoms at the flame-base/log-top
        seam). Each .tsr-camp-smk is wrapped in-place in its own translate g
        (two wisps paint BEHIND the flames, three in front — a shared
        wrapper would break that depth).
     3) Teaser scroll-gate: parked at top, rides in on scroll, dust poof out.
     4) Hotspot tune: per-product PDP hotspot overrides.
     5) Bar energy v4: timeshared cart progress + salt drift on the bar.
     Reduced motion: all decorative motion skipped.
     Kill switch: window.TSR_SPARKLE_PLUS = { overlay:false, stars:false, gate:false, hotspots:false, bar:false }. */
  const FLAGS = (window.TSR_SPARKLE_PLUS = window.TSR_SPARKLE_PLUS || { overlay: true, stars: true, gate: true, hotspots: true, bar: true });
  if (FLAGS.gate === undefined) FLAGS.gate = true;
  if (FLAGS.hotspots === undefined) FLAGS.hotspots = true;
  if (FLAGS.bar === undefined) FLAGS.bar = true;
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const DESK = window.matchMedia('(min-width: 990px)');
  const sleep = (ms) => new Promise((r) => window.setTimeout(r, ms));

  /* ---------------- overlay grains ---------------- */
  const attachOverlay = (host, bgEl) => {
    if (!host || host.dataset.sparklePlus === 'true') return;
    /* 2026-08-27 PageSpeed (forced-reflow): all layout/style READS happen up here,
       before the canvas insertion WRITE below — the old order (insert, then
       getComputedStyle, then clientWidth in size()) forced a synchronous reflow
       per host. The pre-write measurements are safe to reuse for the initial
       canvas size: the canvas is position:absolute, so inserting it never
       changes the host's box. */
    const hostW = host.clientWidth;
    const hostH = host.clientHeight;
    if (hostW < 120) return;
    const bg = getComputedStyle(bgEl || host).backgroundColor.match(/\d+/g) || [255, 255, 255];
    host.dataset.sparklePlus = 'true';

    const canvas = document.createElement('canvas');
    canvas.className = 'tsr-salt-canvas tsr-sparkle-plus-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    const base = host.querySelector('canvas.tsr-salt-canvas');
    if (base) base.after(canvas);
    else host.insertBefore(canvas, host.firstChild);

    const ctx = canvas.getContext('2d');
    if (!ctx) { canvas.remove(); return; }

    const lum = (0.299 * bg[0] + 0.587 * bg[1] + 0.114 * bg[2]) / 255;
    const dark = lum < 0.5;
    const color = dark ? '#EDE5DC' : '#42311F';
    const glintCore = dark ? '#FFFDF7' : '#FFFFFF';
    const mobile = !DESK.matches;

    let W, H, raf = null, grains = [], running = false;
    let nextSpark = 10 + Math.random() * 30;

    const size = () => { W = canvas.width = host.clientWidth; H = canvas.height = host.clientHeight; };

    const reset = (g, top) => {
      g.x = Math.random() * W;
      g.y = top ? -4 : Math.random() * H;
      g.s = 1.1 + Math.random() * 2.6;
      if (Math.random() < 0.06) g.s += 1.2;
      g.v = 0.12 + Math.random() * 0.4;
      g.dx = (Math.random() - 0.5) * 0.12;
      g.o = 0.15 + Math.random() * 0.27;
      g.ph = Math.random() * 6.283;
      g.ga = Math.random() * 1.57;
      g.fl = 0;
    };

    const build = () => {
      grains = [];
      const n = Math.max(2, Math.min(mobile ? 12 : 22, Math.round(W / (mobile ? 85 : 70))));
      for (let i = 0; i < n; i++) { const g = {}; reset(g, false); grains.push(g); }
    };

    const drawGlint = (cx, cy, s, k, a) => {
      const reach = s * (1.3 + k * 2);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(a);
      if (dark) {
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.06 * k;
        ctx.fillStyle = '#F5D9A6';
        ctx.beginPath();
        ctx.arc(0, 0, reach * 0.85, 0, 6.283);
        ctx.fill();
      }
      ctx.globalAlpha = (dark ? 0.3 : 0.2) * k;
      ctx.fillStyle = glintCore;
      ctx.beginPath();
      ctx.moveTo(0, -reach);
      ctx.lineTo(s * 0.45, 0);
      ctx.lineTo(0, reach);
      ctx.lineTo(-s * 0.45, 0);
      ctx.closePath();
      ctx.moveTo(-reach, 0);
      ctx.lineTo(0, s * 0.45);
      ctx.lineTo(reach, 0);
      ctx.lineTo(0, -s * 0.45);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = (dark ? 0.55 : 0.38) * k;
      ctx.beginPath();
      ctx.arc(0, 0, 0.7 + k * 1.2, 0, 6.283);
      ctx.fill();
      ctx.restore();
    };

    /* 2026-09-02 PageSpeed pass 3: ~30fps draw — every other rAF only
       re-schedules; movement and frame counters below are doubled so the
       drift/glint pacing looks identical to the old 60fps loop. Matches the
       same cap in tsr-interactions.js initSaltDrift. */
    let half = false;
    const step = () => {
      if (!running) return;
      half = !half;
      if (half) { raf = window.requestAnimationFrame(step); return; }
      ctx.clearRect(0, 0, W, H);
      if (grains.length && (nextSpark -= 2) <= 0) {
        const sg = grains[(Math.random() * grains.length) | 0];
        sg.fd = (24 + Math.pow(Math.random(), 2) * 168) | 0;
        sg.fl = sg.fd;
        sg.fk = 0.45 + Math.random() * 0.75;
        nextSpark = 40 + Math.random() * 44;
        if (Math.random() < 0.18) nextSpark = 6 + Math.random() * 10;
      }
      for (let i = 0; i < grains.length; i++) {
        const g = grains[i];
        g.y += g.v * 2;
        g.x += (g.dx + Math.sin((g.y + g.ph * 97) * 0.018) * 0.09) * 2;
        if (g.y > H + 4) reset(g, true);
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = g.o;
        ctx.fillStyle = color;
        ctx.fillRect(g.x, g.y, g.s, g.s);
        if (g.fl > 0) {
          const p = (g.fd - g.fl) / g.fd;
          drawGlint(g.x + g.s / 2, g.y + g.s / 2, g.s, g.fk * Math.sin(p * 3.14159), g.ga);
          g.fl = g.fl > 2 ? g.fl - 2 : 0;
        }
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      raf = window.requestAnimationFrame(step);
    };

    /* 2026-09-02 (Ivy: window-resize is "very choppy and laggy" vs competitors):
       this handler ran on EVERY resize event — dozens per second while dragging
       the window edge — and each run is expensive: size() reads clientWidth/Height
       (forced layout mid-resize), the canvas.width assignment clears and
       reallocates the backing store, and build() re-seeds every grain. Multiplied
       by one listener per salt canvas on the page, this was the main source of
       resize jank. Debounced to the 150ms settle; during the drag the canvas just
       stretches with its CSS box (grains scale slightly for a beat — invisible in
       practice), then re-snaps. Same fix applied to the base engine in
       tsr-interactions.js. */
    let resizeSettle;
    window.addEventListener('resize', () => { window.clearTimeout(resizeSettle); resizeSettle = window.setTimeout(() => { size(); build(); }, 150); });
    W = canvas.width = hostW; H = canvas.height = hostH; /* from the pre-write reads; size() stays for resize */
    build();
    /* 2026-08-27 PageSpeed: the drift loop used to run at 60fps forever, even with
       the host scrolled away — on a page dwell most of that work animated nothing
       visible (same lesson as initSaltDrift's observer in tsr-interactions.js).
       Now the loop only runs while the host intersects the viewport. */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !running) { running = true; step(); }
          else if (!entry.isIntersecting && running) { running = false; if (raf) window.cancelAnimationFrame(raf); raf = null; }
        });
      });
      io.observe(host);
    } else { running = true; step(); }
  };

  /* ---------------- footer scene stars ---------------- */
  const STARS = [
    [196, 161, 0.5, 0.3], [207, 172, 0.5, 0.3], [218, 160, 0.6, 0.4],
    [226, 171, 0.7, 0.5], [243, 163, 0.9, 0.65], [258, 178, 0.6, 0.4],
    [276, 160, 1.1, 0.8], [291, 183, 0.6, 0.4], [305, 168, 0.8, 0.55],
    [330, 175, 0.7, 0.45], [348, 161, 1.0, 0.7], [365, 181, 0.6, 0.4],
    [382, 166, 0.8, 0.55], [398, 158, 0.7, 0.5], [430, 170, 0.6, 0.4],
    [447, 161, 0.9, 0.6], [462, 176, 0.7, 0.45], [470, 164, 0.6, 0.4]
  ];
  const TWINKLE = [3, 6, 8, 10, 13, 15];
  const HX = 312, HY = 166;

  const addStars = () => {
    const svg = document.querySelector('.tsr-camp svg');
    if (!svg) return null;
    const old = svg.querySelector('.tsr-stars-plus');
    if (old) old.remove();
    const NS = 'http://www.w3.org/2000/svg';
    const g = document.createElementNS(NS, 'g');
    g.setAttribute('class', 'tsr-stars-plus');
    g.setAttribute('fill', '#fff3dc');
    STARS.forEach((s, i) => {
      const c = document.createElementNS(NS, 'circle');
      c.setAttribute('cx', s[0]);
      c.setAttribute('cy', s[1]);
      c.setAttribute('r', s[2]);
      c.setAttribute('opacity', s[3]);
      if (!REDUCED.matches && TWINKLE.indexOf(i) !== -1) {
        const a = document.createElementNS(NS, 'animate');
        a.setAttribute('attributeName', 'opacity');
        a.setAttribute('values', s[3] + ';' + (s[3] * 0.4).toFixed(2) + ';' + s[3]);
        a.setAttribute('dur', (3.2 + (i % 7) * 0.55).toFixed(2) + 's');
        a.setAttribute('begin', (i * 0.63 % 4).toFixed(2) + 's');
        a.setAttribute('repeatCount', 'indefinite');
        c.appendChild(a);
      }
      g.appendChild(c);
    });
    const hero = document.createElementNS(NS, 'path');
    hero.setAttribute('d', 'M312 160.6 L313.2 164.8 L317.4 166 L313.2 167.2 L312 171.4 L310.8 167.2 L306.6 166 L310.8 164.8 Z');
    hero.setAttribute('class', 'tsr-star-hero');
    hero.setAttribute('opacity', REDUCED.matches ? '0.6' : '0');
    g.appendChild(hero);
    const skyRect = svg.querySelector('rect');
    if (skyRect && skyRect.nextSibling) svg.insertBefore(g, skyRect.nextSibling);
    else svg.appendChild(g);
    return g;
  };

  /* Smoke re-seat (Koby 2026-07-06): the base layer draws the campfire; wisp bottoms hovered
     above the flame base. Wrap each .tsr-camp-smk IN PLACE in its own seat group translated
     5 units down (12 was too deep — logs paint before ALL smoke, so bottoms draped the log
     faces; 5 ends them at the flame-base/log-top seam) — preserves the behind/in-front flame
     paint order AND the CSS sway animation on the paths (ancestor transform composes; a
     transform attribute on the paths themselves would be overridden by the animation).
     Idempotent via parent-class guard; re-seats if the value changes. */
  const SMOKE_SEAT_Y = 5;
  const seatSmoke = (near) => {
    const svg = (near && (near.ownerSVGElement || (near.closest && near.closest('svg')))) || document.querySelector('.tsr-camp svg');
    if (!svg) return;
    const NS = 'http://www.w3.org/2000/svg';
    svg.querySelectorAll('path.tsr-camp-smk').forEach((p) => {
      const t = 'translate(0,' + SMOKE_SEAT_Y + ')';
      if ((p.parentElement.getAttribute('class') || '') === 'tsr-smk-seat') {
        p.parentElement.setAttribute('transform', t);
        return;
      }
      const seat = document.createElementNS(NS, 'g');
      seat.setAttribute('class', 'tsr-smk-seat');
      seat.setAttribute('transform', t);
      p.parentNode.insertBefore(seat, p);
      seat.appendChild(p);
    });
  };

  const startGlimmer = (g) => {
    if (!g || g.dataset.glimmer === 'true' || REDUCED.matches) return;
    g.dataset.glimmer = 'true';
    const NS = 'http://www.w3.org/2000/svg';
    const stars = Array.prototype.slice.call(g.querySelectorAll('circle'));
    if (!stars.length) return;
    const flash = () => {
      if (!g.isConnected) return;
      const c = stars[(Math.random() * stars.length) | 0];
      const cx = +c.getAttribute('cx');
      const cy = +c.getAttribute('cy');
      const r = +c.getAttribute('r');
      const baseO = +c.getAttribute('opacity') || 0.5;
      const k = 0.45 + Math.random() * 0.75;
      const reach = r * (2.2 + k * 2.6);
      const arm = r * 0.5;
      const p = document.createElementNS(NS, 'path');
      p.setAttribute('d',
        'M' + cx + ' ' + (cy - reach) + ' L' + (cx + arm) + ' ' + cy + ' L' + cx + ' ' + (cy + reach) + ' L' + (cx - arm) + ' ' + cy + ' Z ' +
        'M' + (cx - reach) + ' ' + cy + ' L' + cx + ' ' + (cy + arm) + ' L' + (cx + reach) + ' ' + cy + ' L' + cx + ' ' + (cy - arm) + ' Z');
      p.setAttribute('fill', '#FFFDF7');
      p.setAttribute('opacity', '0');
      g.appendChild(p);
      const dur = 500 + Math.random() * 900;
      try {
        p.animate([{ opacity: 0 }, { opacity: 0.85 * k }, { opacity: 0 }], { duration: dur, easing: 'ease-in-out' });
        c.animate([{ opacity: baseO }, { opacity: Math.min(1, baseO + 0.5) }, { opacity: baseO }], { duration: dur, easing: 'ease-in-out' });
      } catch (error) { /* SMIL twinkles still carry the motion */ }
      window.setTimeout(() => p.remove(), dur + 80);
      const next = (Math.random() < 0.18) ? 180 + Math.random() * 240 : 900 + Math.random() * 1600;
      window.setTimeout(flash, next);
    };
    window.setTimeout(flash, 700 + Math.random() * 900);
  };

  const heroLife = (g) => {
    const hero = g.querySelector('.tsr-star-hero');
    if (!hero || hero.dataset.life === 'true' || REDUCED.matches) return;
    hero.dataset.life = 'true';
    const NS = 'http://www.w3.org/2000/svg';
    let shimmering = false;

    const fadeTo = (el, from, to, dur) => new Promise((res) => {
      try {
        const a = el.animate([{ opacity: from }, { opacity: to }], { duration: dur, easing: 'ease-in-out', fill: 'forwards' });
        a.onfinish = () => { el.setAttribute('opacity', String(to)); try { a.cancel(); } catch (e) {} res(); };
        a.oncancel = () => res();
      } catch (e) { el.setAttribute('opacity', String(to)); res(); }
    });

    (async () => {
      await sleep(1200 + Math.random() * 2600);
      while (g.isConnected) {
        if (!shimmering) {
          const peak = 0.55 + Math.random() * 0.4;
          await fadeTo(hero, +hero.getAttribute('opacity') || 0, peak, 1600 + Math.random() * 1700);
          await sleep(350 + Math.random() * 750);
          if (!shimmering) await fadeTo(hero, peak, 0, 2100 + Math.random() * 2100);
        }
        await sleep(2600 + Math.random() * 5800);
      }
    })();

    /* Shooting star — replaces the in-place bright burst (Koby 2026-07-05).
       A warm meteor streaks the sky band (x≈196–470, y≈152–196): cream head,
       #F5D9A6 tapering tail, gentle arc, tiny embers left behind; the hero
       star glows up in response as it passes. Same cadence as the old burst
       (5s dwell · 75% · 6–9s re-arm) — "not less than the star is now."
       Time-based rAF; cleanup ALWAYS via safety timeout, never gated on
       animation callbacks (hidden-tab rule). Renders inside the stars group,
       so it passes BEHIND the scene's foreground silhouettes. */
    const shootingStar = () => {
      if (shimmering || !g.isConnected) return;
      shimmering = true;
      const ltr = Math.random() < 0.5;
      const x0 = ltr ? 200 + Math.random() * 60 : 405 + Math.random() * 60;
      const y0 = 152 + Math.random() * 14;
      const dx = (125 + Math.random() * 70) * (ltr ? 1 : -1);
      const dy = 9 + Math.random() * 13;
      const bow = 2.5 + Math.random() * 4.5;
      const dur = 700 + Math.random() * 450;
      const L = 26 + Math.random() * 14;

      const sg = document.createElementNS(NS, 'g');
      sg.setAttribute('class', 'tsr-shoot');
      const mkLine = (w, col) => {
        const l = document.createElementNS(NS, 'line');
        l.setAttribute('stroke', col);
        l.setAttribute('stroke-width', w);
        l.setAttribute('stroke-linecap', 'round');
        l.setAttribute('opacity', '0');
        sg.appendChild(l);
        return l;
      };
      const tail = mkLine(1.1, '#F5D9A6');
      const core = mkLine(0.55, '#FFFDF7');
      const halo = document.createElementNS(NS, 'circle');
      halo.setAttribute('r', '2.6'); halo.setAttribute('fill', '#F5D9A6'); halo.setAttribute('opacity', '0');
      sg.appendChild(halo);
      const head = document.createElementNS(NS, 'circle');
      head.setAttribute('r', '1.2'); head.setAttribute('fill', '#FFFDF7'); head.setAttribute('opacity', '0');
      sg.appendChild(head);
      g.appendChild(sg);

      try {
        hero.animate([{ opacity: +hero.getAttribute('opacity') || 0 }, { opacity: 0.85 }, { opacity: 0 }], { duration: dur + 500, easing: 'ease-in-out', fill: 'forwards' });
      } catch (e) { /* meteor carries the moment on its own */ }

      let embers = 0;
      let lastEmber = 0;
      const nowMs = () => (window.performance && performance.now) ? performance.now() : Date.now();
      const t0 = nowMs();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        sg.remove();
        hero.setAttribute('opacity', '0');
        shimmering = false;
      };

      const frame = () => {
        if (done) return;
        if (!g.isConnected) { finish(); return; }
        const p = (nowMs() - t0) / dur;
        if (p >= 1) { finish(); return; }
        const e = 1 - Math.pow(1 - p, 1.6);
        const x = x0 + dx * e;
        const y = y0 + dy * e + bow * Math.sin(e * 3.14159);
        const tx = dx;
        const ty = dy + bow * 3.14159 * Math.cos(e * 3.14159);
        const m = Math.sqrt(tx * tx + ty * ty) || 1;
        const ux = tx / m;
        const uy = ty / m;
        const env = p < 0.12 ? p / 0.12 : (p > 0.7 ? (1 - p) / 0.3 : 1);
        tail.setAttribute('x1', x); tail.setAttribute('y1', y);
        tail.setAttribute('x2', x - ux * L * e); tail.setAttribute('y2', y - uy * L * e);
        tail.setAttribute('opacity', (0.32 * env).toFixed(3));
        core.setAttribute('x1', x); core.setAttribute('y1', y);
        core.setAttribute('x2', x - ux * L * 0.55 * e); core.setAttribute('y2', y - uy * L * 0.55 * e);
        core.setAttribute('opacity', (0.8 * env).toFixed(3));
        halo.setAttribute('cx', x); halo.setAttribute('cy', y);
        halo.setAttribute('opacity', (0.22 * env).toFixed(3));
        head.setAttribute('cx', x); head.setAttribute('cy', y);
        head.setAttribute('r', (1.1 + 0.5 * Math.sin(p * 3.14159)).toFixed(2));
        head.setAttribute('opacity', (0.95 * env).toFixed(3));
        if (p > 0.25 && embers < 3 && nowMs() - lastEmber > 130 && Math.random() < 0.2) {
          embers++;
          lastEmber = nowMs();
          const d = document.createElementNS(NS, 'circle');
          d.setAttribute('cx', x - ux * (3 + Math.random() * 6));
          d.setAttribute('cy', y - uy * (3 + Math.random() * 6) + (Math.random() - 0.5) * 1.6);
          d.setAttribute('r', (0.5 + Math.random() * 0.4).toFixed(2));
      d.setAttribute('fill', '#FFF3DC');
          d.setAttribute('opacity', '0.7');
          g.appendChild(d);
          try { d.animate([{ opacity: 0.7 }, { opacity: 0 }], { duration: 550 + Math.random() * 350, easing: 'ease-out', fill: 'forwards' }); } catch (er) { /* fades via removal */ }
          window.setTimeout(() => d.remove(), 950);
        }
        window.requestAnimationFrame(frame);
      };
      window.requestAnimationFrame(frame);
      window.setTimeout(finish, dur + 900); /* hidden-tab safety — never gate cleanup on rAF/animation callbacks */
    };

    const svg = g.ownerSVGElement || g.closest('svg');
    let inView = false;
    let dwell = null;
    const arm = (delay) => {
      window.clearTimeout(dwell);
      dwell = window.setTimeout(() => {
        if (inView && document.visibilityState === 'visible' && Math.random() < 0.75) shootingStar();
        if (inView) arm(6000 + Math.random() * 3000);
      }, delay);
    };
    try {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const now = e.isIntersecting && e.intersectionRatio >= 0.35;
          if (now && !inView) { inView = true; arm(5000); }
          else if (!now && inView) { inView = false; window.clearTimeout(dwell); }
        });
      }, { threshold: [0, 0.35, 0.7] });
      io.observe(svg);
    } catch (error) { /* no dwell meteor without IO */ }
  };

  /* ---------------- hotspot tune (per-product) ---------------- */
  const HS_MAP = {
    'grinder-jar-attachment': { replace: [
      { left: '47%', top: '27%', t: 'Adjustable grind', b: 'Turn the inner mechanism — fine for eggs & pasta, coarser for steak.' },
      { left: '75%', top: '40%', t: 'One cap, total control', b: 'Move it between jars whenever the craving changes.' },
      { left: '25%', top: '78%', t: 'Fits every 4oz jar', b: 'Twist it on in place of the lid — no tools, no transferring, no mess.' }
    ] },
    'the-salted-roots-earrings': { hide: true },
    'red-wine-salt': { first: { t: 'Steeped in red wine', b: 'Garlic, peppercorn, lemon + savory herbs — infused to the core.' } },
    'tequila-salt': { first: { t: 'Real tequila + lime', b: 'Tomatillo, garlic, spicy pepper — eggs on Tuesday, margarita rims on Friday.' } },
    'smoked-thai-basil-salt': { first: { t: 'Sauvignon blanc + Thai basil', b: 'Lemongrass, pomegranate, lemon peel — bright and unexpected.' } },
    'breakfast-salt': { first: { t: 'Whiskey + twice-smoked bacon', b: 'Whiskey, real maple, vanilla bean — cherry & applewood smoke.' } },
    'bourbon-sugar': { first: { t: 'Bourbon + vanilla bean', b: 'Orange in raw turbinado sugar, warm on the finish.' } },
    'bluebird-dandelion-sugar': { first: { t: 'Wild dandelion + butterfly pea', b: 'Rosehips + raspberry in raw sugar — delicate and floral.' } },
    'dill-pickle-salt': { first: { t: 'Bold + briny', b: 'All the tang of the pickle jar, none of the juice.' } },
    'prickly-chipotle-salt': { first: { t: 'Prickly pear + morita chipotle', b: 'Ancho Reyes liqueur — sweet, smoky, just spicy enough.' } },
    'red-rock-zen': { first: { t: 'Real wasabi + miso', b: 'Ginger + Thai chili — earthy and umami-forward.' } },
    'giving-thanks-salt': { first: { t: "Grandma's stuffing, in salt form", b: 'Warm, comforting, nostalgic — the holiday table in a sprinkle.' } },
    'pumpkin-spice-sugar': { first: { t: 'Cinnamon whiskey infusion', b: 'Real pumpkin, clove, allspice, nutmeg, ginger — never a candle.' } },
    'wasatch-orchard-spice': { first: { t: 'Utah apple cider + chai', b: 'Real cranberries + dried apples — October at the farmers market.' } },
    'summer-salt': { first: { t: 'White peach balsamic', b: 'Sun-dried tomato, basil, honey — the best day of summer.' } },
    'lavender-luau': { first: { t: 'Toasted coconut + lavender', b: 'Mango, pineapple, hibiscus — floral, tropical, does a lot.' } },
    'truffle-salt': { first: { t: 'Real black truffle', b: 'Prosecco + Celtic sea salt — the last thing you add, the first thing they taste.' } },
    'truffle-salt-grinder': { first: { t: 'Black truffle + Prosecco', b: 'Refined and a little indulgent — with a custom wood grinder.' } }
  };

  const setSpot = (s, t, b) => {
    const st = s.querySelector('.tsr-hs__card strong');
    const bd = s.querySelector('.tsr-hs__card span');
    if (st) st.textContent = t;
    if (bd) bd.textContent = b;
    s.setAttribute('aria-label', t + '. ' + b);
  };

  const hotspotTune = () => {
    const m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return true;
    const cfg = HS_MAP[m[1]];
    if (!cfg) return true;
    const spots = Array.prototype.slice.call(document.querySelectorAll('button.tsr-hs'));
    if (!spots.length) return false; /* injected later — retry */
    if (cfg.hide) {
      spots.forEach((s) => { s.style.display = 'none'; });
      return true;
    }
    if (cfg.replace) {
      spots.forEach((s, i) => {
        const r = cfg.replace[i];
        if (!r) { s.style.display = 'none'; return; }
        s.style.left = r.left;
        s.style.top = r.top;
        setSpot(s, r.t, r.b);
      });
      return true;
    }
    if (cfg.first) {
      const target = spots.find((s) => /truly infused/i.test(s.getAttribute('aria-label') || ''));
      if (target) setSpot(target, cfg.first.t, cfg.first.b);
    }
    return true;
  };

  /* ---------------- masthead hero buttons ---------------- */
  const heroButtons = () => {
    const hb = document.querySelector('.masthead-hero__button--primary');
    if (hb && !REDUCED.matches) attachOverlay(hb, hb);
    /* 2026-08-19: the secondary button's glass treatment was removed here.
       It set `background` inline with `important` plus mouseenter/mouseleave
       handlers, which no stylesheet can override (inline important outranks
       stylesheet important). That button is now a white pearl owned entirely
       by assets/tsr-hero-pearl.css, including its hover and active states.
       Restoring the glass look means reverting that file too, not just this
       block. The primary button's sparkle overlay above is untouched. */
  };

  /* ---------------- announcement bar energy ---------------- */
  const BAR_THRESHOLD = 5000; /* cents — keep in sync with main-cart-footer.liquid (tsr_free_threshold).
     Was 6000 and had drifted out of sync: the cart page read $50 while this banner
     overlay told customers $60. Reported by Koby 2026-08-14. */
  const barEnergy = () => {
    const sect = document.querySelector('[id$="__announcement-bar"]');
    if (!sect || sect.dataset.tsrBar === 'true') return false;
    sect.dataset.tsrBar = 'true';

    const isOpaque = (el) => {
      const b = getComputedStyle(el).backgroundColor;
      return b && b !== 'transparent' && b.indexOf('rgba(0, 0, 0, 0)') === -1;
    };
    let bgEl = isOpaque(sect) ? sect : null;
    if (!bgEl) {
      const all = sect.querySelectorAll('*');
      for (let i = 0; i < all.length; i++) { if (isOpaque(all[i])) { bgEl = all[i]; break; } }
    }
    if (!bgEl) bgEl = sect;
    const bgColor = getComputedStyle(bgEl).backgroundColor;

    if (!REDUCED.matches) attachOverlay(sect, bgEl);

    if (window.location.pathname.indexOf('/cart') === 0) return true;

    let overlay = null;
    let hideTimer = null;
    let cycleTimer = null;
    const SHOW_MS = 4500;
    const REST_MS = 9000;

    const clearTimers = () => { window.clearTimeout(hideTimer); window.clearTimeout(cycleTimer); };

    const makeOverlay = () => {
      const o = document.createElement('a');
      o.className = 'tsr-bar-progress';
      o.href = '/collections/shop-all';
      o.style.background = bgColor;
      sect.appendChild(o);
      return o;
    };

    const setAwake = (o, awake) => {
      o.style.opacity = awake ? '1' : '0';
      o.style.pointerEvents = awake ? '' : 'none';
      o.style.visibility = awake ? 'visible' : 'hidden';
      if (!REDUCED.matches) {
        try { o.animate([{ opacity: awake ? 0 : 1 }, { opacity: awake ? 1 : 0 }], { duration: awake ? 350 : 400, easing: 'ease' }); } catch (e) { /* no-op */ }
      }
    };

    const removeOverlay = () => {
      clearTimers();
      if (!overlay) return;
      const o = overlay;
      overlay = null;
      if (!REDUCED.matches) {
        try { o.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 450, easing: 'ease' }); } catch (e) { /* no-op */ }
      }
      window.setTimeout(() => o.remove(), REDUCED.matches ? 0 : 460);
    };

    const startCycle = () => {
      clearTimers();
      cycleTimer = window.setTimeout(() => {
        if (!overlay) return;
        setAwake(overlay, false);
        cycleTimer = window.setTimeout(() => {
          if (!overlay) return;
          setAwake(overlay, true);
          startCycle();
        }, REST_MS);
      }, SHOW_MS);
    };

    const render = (cart) => {
      const count = cart.item_count | 0;
      const total = cart.total_price | 0;
      clearTimers();
      if (count < 1) { removeOverlay(); return; }

      if (total >= BAR_THRESHOLD) {
        let shown = false;
        try { shown = window.sessionStorage.getItem('tsr-bar-met-shown') === '1'; } catch (e) {}
        if (shown) { removeOverlay(); return; }
        if (!overlay) overlay = makeOverlay();
        overlay.classList.add('tsr-bar-progress--met');
        overlay.innerHTML = '<span>✓ Free shipping unlocked</span>';
        setAwake(overlay, true);
        hideTimer = window.setTimeout(() => {
          try { window.sessionStorage.setItem('tsr-bar-met-shown', '1'); } catch (e) {}
          removeOverlay();
        }, 4500);
        return;
      }

      if (!overlay) overlay = makeOverlay();
      overlay.classList.remove('tsr-bar-progress--met');
      const remain = BAR_THRESHOLD - total;
      const pct = Math.max(4, Math.round((total * 100) / BAR_THRESHOLD));
      const dollars = (remain % 100 === 0) ? String(remain / 100) : (remain / 100).toFixed(2);
      // Reuse the existing nodes rather than reprinting the markup. Rebuilding via
      // innerHTML handed the browser a brand-new fill span on every update, and a
      // transition never fires on an element's first render -- which is why
      // `transition: width .5s ease` (tsr-conversion.css) has been inert. Now the
      // same span survives between renders, so a cart change animates the width.
      // The --met branch prints different markup, so switching modes drops these
      // nodes and the rebuild below picks it up.
      let barLabel = overlay.querySelector('.tsr-bar-progress__label');
      let barFill = overlay.querySelector('.tsr-bar-progress__fill');
      if (!barLabel || !barFill) {
        overlay.innerHTML = '<span class="tsr-bar-progress__label"></span>' +
          '<span class="tsr-bar-progress__track"><span class="tsr-bar-progress__fill"></span></span>';
        barLabel = overlay.querySelector('.tsr-bar-progress__label');
        barFill = overlay.querySelector('.tsr-bar-progress__fill');
        // First paint lands on the real value: no 0 -> pct sweep on every page load.
        barFill.style.width = pct + '%';
      } else {
        barFill.style.width = pct + '%';
      }
      barLabel.textContent = 'You’re $' + dollars + ' away from free shipping';
      try { window.sessionStorage.removeItem('tsr-bar-met-shown'); } catch (e) {}
      setAwake(overlay, true);
      startCycle();
    };

    const refresh = () => {
      fetch('/cart.js').then((r) => r.json()).then(render).catch(() => {});
    };
    refresh();
    try {
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refresh);
      }
    } catch (e) { /* no pub/sub — focus refresh still covers it */ }
    window.addEventListener('focus', refresh);
    return true;
  };

  /* 2026-08-27 PageSpeed (mainthread-work): the "keep trying until the element
     exists" fallbacks were body-wide subtree MutationObservers that ran their
     callback on EVERY DOM mutation for up to 15s — right through the load
     mutation storm (reveals, carousels, review app, pixels). A 400ms poll does
     the same job in ~35 cheap querySelector calls, worst case. */
  const retryUntil = (fn) => {
    const t = window.setInterval(() => { if (fn()) window.clearInterval(t); }, 400);
    window.setTimeout(() => window.clearInterval(t), 15000);
  };

  /* Split boot (2026-08-27 PageSpeed): initLight = things a user can notice early
     (the Save-10% tab must be parked BEFORE tsr-interactions.js appends it at
     DCL+1200ms, or it flashes at the top of the page; PDP hotspot copy should be
     right before anyone opens a hotspot). Still boots at DCL+120ms like before.
     initHeavy = pure decoration (grain canvases, footer stars, bar overlay) —
     that now waits for window load + an idle slot, so it stops competing with
     gtag/fbevents/wpm inside the stretch Lighthouse scores as TBT. Visual cost:
     sparkle layers fade in ~1s later on a cold load. */
  const initLight = () => {
    if (FLAGS.gate && !teaserGate()) retryUntil(teaserGate);
    if (FLAGS.hotspots && !hotspotTune()) retryUntil(hotspotTune);
  };

  const initHeavy = () => {
    /* 2026-09-02 PageSpeed pass 3: weak hardware (<=4 cores or <=4GB reported —
       the budget-Android class the mobile audit emulates) skips the DECORATIVE
       canvases and footer-star extras, same contract as reduced-motion.
       heroButtons() and barEnergy() stay: the pearl/frosted button styling and
       the free-shipping cart meter are functional, not decoration (the
       secondary hero button's frosted background is applied inline HERE and
       nowhere else — see the header comment, item 1). Mirrors the skip in
       tsr-interactions.js initSaltDrift. */
    const weak = (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 4;
    if (FLAGS.overlay && !REDUCED.matches && !weak) {
      document.querySelectorAll('.tsr-salt-host').forEach((h) => attachOverlay(h));
    }
    heroButtons();
    if (FLAGS.stars && !weak) {
      const g = addStars();
      if (g) { startGlimmer(g); heroLife(g); seatSmoke(g); }
      else {
        retryUntil(() => {
          const gg = addStars();
          if (gg) { startGlimmer(gg); heroLife(gg); seatSmoke(gg); return true; }
          return false;
        });
      }
    }
    if (FLAGS.bar) barEnergy();
  };

  /* ---------------- teaser scroll-gate + horse-dust poof ---------------- */
  const teaserGate = () => {
    const tab = document.querySelector('.tsr-salt-tab');
    if (!tab || tab.dataset.spGate === 'true') return false;
    tab.dataset.spGate = 'true';
    const SHOW_AT = 280;
    const HIDE_AT = 140;
    let shown = false;
    let busy = false;

    const park = () => tab.classList.add('tsr-sp-parked');
    const unpark = () => tab.classList.remove('tsr-sp-parked');

    const dust = (rect) => {
      const z = getComputedStyle(tab).zIndex;
      for (let i = 0; i < 6; i++) {
        const d = document.createElement('span');
        d.className = 'tsr-sp-dust';
        const s = 5 + Math.random() * 9;
        d.style.width = d.style.height = s + 'px';
        d.style.left = (rect.left + 6 + Math.random() * rect.width * 0.5) + 'px';
        d.style.top = (rect.top + rect.height * 0.3 + Math.random() * rect.height * 0.55) + 'px';
        if (z && z !== 'auto') d.style.zIndex = z;
        document.body.appendChild(d);
        const dur = 460 + Math.random() * 380;
        const delay = i * 45;
        try {
          d.animate(
            [
              { transform: 'translate(0,0) scale(0.55)', opacity: 0 },
              { opacity: 0.5, offset: 0.25 },
              { transform: 'translate(' + (-26 - Math.random() * 34) + 'px,' + (-8 + Math.random() * 16) + 'px) scale(' + (1.15 + Math.random() * 0.5) + ')', opacity: 0 }
            ],
            { duration: dur, easing: 'cubic-bezier(.2,.6,.4,1)', delay: delay }
          );
        } catch (error) { /* no-op */ }
        window.setTimeout(() => d.remove(), dur + delay + 100);
      }
    };

    const takeOff = () => {
      if (!shown || busy) return;
      shown = false;
      if (REDUCED.matches) { park(); return; }
      busy = true;
      const rect = tab.getBoundingClientRect();
      dust(rect);
      let done = false;
      const finish = () => { if (done) return; done = true; park(); busy = false; };
      try {
        const a = tab.animate(
          [
            { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
            { transform: 'translate(26px,-4px) rotate(3deg)', opacity: 1, offset: 0.35 },
            { transform: 'translate(160px,-10px) rotate(9deg)', opacity: 0 }
          ],
          { duration: 420, easing: 'cubic-bezier(.5,0,.9,.4)' }
        );
        a.onfinish = finish;
        a.oncancel = finish;
      } catch (error) { /* no-op */ }
      window.setTimeout(finish, 600);
    };

    const rideIn = () => {
      if (shown || busy) return;
      shown = true;
      unpark();
      if (REDUCED.matches) return;
      try {
        tab.animate(
          [
            { transform: 'translate(130px,6px) rotate(7deg)', opacity: 0 },
            { transform: 'translate(-6px,0) rotate(-1.5deg)', opacity: 1, offset: 0.75 },
            { transform: 'translate(0,0) rotate(0deg)', opacity: 1 }
          ],
          { duration: 480, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      } catch (error) { /* shown without flourish */ }
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const y = window.scrollY || window.pageYOffset;
        if (y > SHOW_AT) rideIn();
        else if (y < HIDE_AT) takeOff();
      });
    };

    if ((window.scrollY || window.pageYOffset) > SHOW_AT) { shown = true; }
    else { park(); }
    window.addEventListener('scroll', onScroll, { passive: true });
    return true;
  };

  const bootHeavy = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(initHeavy, { timeout: 1500 });
    else window.setTimeout(initHeavy, 300);
  };
  const boot = () => {
    window.setTimeout(initLight, 120);
    if (document.readyState === 'complete') bootHeavy();
    else window.addEventListener('load', bootHeavy, { once: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
